import { SortingState } from '@tanstack/react-table'
import { useMutation, useQuery } from '@/hooks'
import { PaginatedResponse, DataResponse } from '@/models/api'
import { RoleStatus } from '@/constants/role'
import { RoleModel } from '@/models/role/role'
import { datatableSortingStateToQuery } from '@/utils/api'
import { useQueryClient } from '@tanstack/react-query'

export const useCreateRole = () => {
  const queryClient = useQueryClient()
  return useMutation<
    any,
    Error,
    {
      name: string
      key: string
      description: string
      status: string
      permissionIds: string[]
      max_drivice: number
    }
  >(
    ['create_role'],
    {
      url: '/roles/create',
      method: 'POST',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['roles'] })
      },
    },
  )
}

export const useUpdateRole = () => {
  const queryClient = useQueryClient()
  return useMutation<
    any,
    Error,
    {
      id: string
      name: string
      description: string
      permissionIds: string[]
      max_drivice: number
    }
  >(
    ['update_role'],
    {
      url: '/roles/:id',
      method: 'PUT',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['roles'] })
      },
    },
  )
}

export const useDeleteRole = () => {
  const queryClient = useQueryClient()
  return useMutation<any, Error, { id: string }>(
    ['delete_role'],
    {
      url: '/roles/:id',
      method: 'DELETE',
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['roles'] })
      },
    },
  )
}

type UseGetRole = {
  page?: number
  pageSize?: number
  sort?: SortingState
  search?: string
  status?: string
}

export const useGetRoleList = ({ page, pageSize, sort, search, status }: UseGetRole) => {
  return useQuery<PaginatedResponse<RoleModel>>({
    queryKey: ['roles', { page, pageSize, sort, search, status }],
    url: '/roles',
    axiosConfig: {
      params: {
        page,
        pageSize,
        search,
        status: status === RoleStatus.ALL ? '' : status,
        sort: datatableSortingStateToQuery(sort),
      },
    },
  })
}

export const useGetRoleListNoPage = () => {
  return useQuery<PaginatedResponse<RoleModel>>({
    queryKey: ['roles_no_page'],
    url: '/roles/no-page',
  })
}

export const useGetPermissionList = () => {
  return useQuery<
    DataResponse<
      {
        id: string
        name: string
        description: string
        created_at: string
        updated_at: string
        key: string
        actions: string[]
        default: boolean
        group: string
      }[]
    >
  >({
    queryKey: ['permissions_list'],
    url: '/permissions',
  })
}

export const useGetRoleById = (id: string) => {
  return useQuery<RoleModel>({
    queryKey: ['role_by_id', id],
    url: `/roles/${id}`,
  })
}
